<template>
  <div class="relative">
    <MapaBase :editing="false" :saveCenter="false" :search="false" height="60vh" @ready="onReady" />
    <div class="leyenda-mapa">
      <div class="leyenda-item">
        <span class="leyenda-color" style="background: #86efac; border-color: #16a34a"></span>
        Disponible
      </div>
      <div class="leyenda-item">
        <span class="leyenda-color" style="background: #fde68a; border-color: #d97706; border-style: dashed"></span>
        Nunca trabajado
      </div>
      <div class="leyenda-item">
        <span class="leyenda-color" style="background: #fca5a5; border-color: #dc2626"></span>
        Asignado
      </div>
      <div class="leyenda-item">
        <span class="leyenda-color" style="background: #60a5fa; border-color: #2563eb"></span>
        Seleccionado
      </div>
      <div class="leyenda-item">
        <span class="leyenda-manzana" style="opacity: 1">12</span>
        Manzana pendiente
      </div>
      <div class="leyenda-item">
        <span class="leyenda-manzana" style="opacity: 0.3">12</span>
        Manzana completada
      </div>
    </div>
  </div>
</template>

<script setup>
import { onUnmounted, watch, defineProps, defineEmits } from "vue";
import MapaBase from "@/components/MapaBase/MapaBase.vue";
import { useTerritoriosStore } from "@/store/territorios";
import { useTrabajoStore } from "@/store/useTrabajoStore";

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  selected: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["selected"]);

const territorios = useTerritoriosStore();
const trabajo = useTrabajoStore();

let L = null;
let mapInstance = null;
let polygonGroup = null;
let mzGroup = null;
const polygonsByName = {};

// dashArray se declara explícito (null incluido) en las 4 variantes: Leaflet
// mergea estilos con setStyle() y no limpia claves ausentes, así que sin esto
// un territorio nunca trabajado quedaría con borde punteado al seleccionarlo.
const ESTILO = {
  disponible: { color: "#16a34a", fillColor: "#86efac", fillOpacity: 0.18, weight: 3, dashArray: null },
  nuncaTrabajado: {
    color: "#d97706",
    fillColor: "#fde68a",
    fillOpacity: 0.28,
    weight: 3,
    dashArray: "8, 6",
  },
  asignado: { color: "#dc2626", fillColor: "#fca5a5", fillOpacity: 0.22, weight: 3, dashArray: null },
  seleccionado: { color: "#2563eb", fillColor: "#60a5fa", fillOpacity: 0.45, weight: 4, dashArray: null },
};

const estiloDe = (item) => {
  if (props.selected.includes(item.name)) return ESTILO.seleccionado;
  if (item.asignado) return ESTILO.asignado;
  if (item.nuncaTrabajado) return ESTILO.nuncaTrabajado;
  return ESTILO.disponible;
};

const escapeHtml = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]));

const tooltipHtml = (item) => {
  const completadas = (item.manzanasTotal || 0) - (item.manzanasPendientesTotal || 0);
  const estadoLabel = item.asignado ? "Asignado" : "Disponible";
  const estadoClase = item.asignado ? "tt-asignado" : "tt-disponible";
  const ultima = item.ultimaCompletacion ? trabajo.getDate(item.ultimaCompletacion) : "—";
  return `
    <div class="territorio-tooltip-body">
      <div class="tt-title">${escapeHtml(item.name)}</div>
      ${item.referencia ? `<div class="tt-ref">${escapeHtml(item.referencia)}</div>` : ""}
      <div class="tt-row">
        <span class="tt-tag ${estadoClase}">${estadoLabel}</span>
        <span>${completadas}/${item.manzanasTotal || 0} manzanas</span>
      </div>
      <div class="tt-row">Última completación: ${ultima}</div>
      <div class="tt-row">Veces completado: ${item.vecesCompletado || 0}</div>
    </div>
  `;
};

const buildManzanaIcon = (mzIdx, completada) =>
  L.divIcon({
    className: "manzana-marker",
    iconSize: null,
    html: `<div class="icon manzana-marker-center" style="font-size: 0.85em; opacity: ${
      completada ? 0.3 : 1
    };">${mzIdx}</div>`,
  });

const draw = () => {
  if (!mapInstance || !L) return;

  if (polygonGroup) polygonGroup.remove();
  if (mzGroup) mzGroup.remove();
  for (const key in polygonsByName) delete polygonsByName[key];

  const polys = [];
  const mzMarkers = [];
  const bounds = [];

  for (const item of props.items) {
    const terr = territorios.byUuid[item.uuid];
    if (!terr?.limits?.length) continue;

    const poly = L.polygon(terr.limits, estiloDe(item));
    poly.on("click", () => emit("selected", item.name));
    poly.bindTooltip(tooltipHtml(item), { sticky: true, className: "territorio-tooltip" });
    polys.push(poly);
    polygonsByName[item.name] = poly;
    bounds.push(...terr.limits);

    const pendientes = new Set((item.manzanasPendientes || []).map(Number));
    for (const key in terr.mzNumbers || {}) {
      const mzIdx = parseInt(key);
      if (mzIdx === 0) continue;
      const completada = !pendientes.has(mzIdx);
      mzMarkers.push(
        L.marker(terr.mzNumbers[key][0], {
          icon: buildManzanaIcon(mzIdx, completada),
          interactive: false,
        })
      );
    }
  }

  polygonGroup = L.featureGroup(polys).addTo(mapInstance);
  mzGroup = L.layerGroup(mzMarkers).addTo(mapInstance);
  if (bounds.length) mapInstance.fitBounds(bounds, { padding: [20, 20] });
};

const restyleOnly = () => {
  for (const item of props.items) {
    const poly = polygonsByName[item.name];
    if (!poly) continue;
    poly.setStyle(estiloDe(item));
    if (props.selected.includes(item.name)) poly.bringToFront();
  }
};

const onReady = ({ m, map }) => {
  L = m().L;
  mapInstance = map();
  draw();
};

watch(() => props.items, draw);
watch(() => props.selected, restyleOnly, { deep: true });

onUnmounted(() => {
  if (polygonGroup) polygonGroup.remove();
  if (mzGroup) mzGroup.remove();
  for (const key in polygonsByName) delete polygonsByName[key];
});
</script>

<style scoped>
.leyenda-mapa {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  padding: 8px 10px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.15);
  font-size: 0.8em;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #1f2937;
}
.leyenda-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.leyenda-color {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 2px solid;
  flex-shrink: 0;
}
.leyenda-manzana {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  padding: 1px 4px;
  border-radius: 2px;
  background-color: var(--icon-bg, rgb(252, 252, 71));
  border: 1px solid var(--icon-borde, rgb(156, 156, 0));
  font-size: 0.85em;
  flex-shrink: 0;
}

:deep(.territorio-tooltip) {
  padding: 6px 8px;
}
:deep(.territorio-tooltip-body) {
  color: #1f2937;
  font-size: 0.8em;
  line-height: 1.4;
}
:deep(.tt-title) {
  font-weight: bold;
  font-size: 1.05em;
}
:deep(.tt-ref) {
  color: #6b7280;
  margin-bottom: 2px;
}
:deep(.tt-row) {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
:deep(.tt-tag) {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 0.9em;
  font-weight: 600;
  color: #fff;
}
:deep(.tt-tag.tt-disponible) {
  background: #16a34a;
}
:deep(.tt-tag.tt-asignado) {
  background: #dc2626;
}
</style>
